/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-this-in-sfc */
import { useQuery } from "@apollo/client";
import type { RefObjectForHighchartsReact } from "components/charts/modern/abstract/HighchartsChart";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import handleClickInLegend from "components/charts/modern/helpers/handleClickInLegend";
import type { AxisScaleType } from "components/charts/modern/inputs/AxisScaleSelector";
import { LogLinearScaleSelector } from "components/charts/modern/inputs/AxisScaleSelector";
import { AreaBarPlotTypeSelector } from "components/charts/modern/inputs/PlotTypeSelector";
import TickerSymbolSelector from "components/charts/modern/inputs/TickerSymbolSelector";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import aeonik from "components/utils/aeonik";
import compareAsc from "date-fns/compareAsc";
import getYear from "date-fns/getYear";
import parseISO from "date-fns/parseISO";
import type Highcharts from "highcharts/highstock";
import type { SeriesAreaOptions, SeriesBarOptions } from "highcharts/highstock";
import PerEmployeeRatiosHistoryQuery from "lib/graphql/queries/PerEmployeeRatiosHistoryQuery";
import type {
  IPerEmployeeRatiosHistoryQuery,
  IPerEmployeeRatiosHistoryQueryVariables,
} from "lib/graphql/queries/__generated__/PerEmployeeRatiosHistoryQuery.generated";
import isSequential from "lib/utils/isSequential";
import { useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";

interface EmployeesChartProps {
  tickerSymbols: string[];
  availableTickerSymbols?: string[];
  onToggleTickerSymbols?: (tickerSymbol: string[]) => void;
}

export default function EmployeesChart({
  tickerSymbols,
  availableTickerSymbols,
  onToggleTickerSymbols,
}: EmployeesChartProps) {
  const intl = useIntl();
  const [plotType, setPlotType] = useState<"area" | "column">("area");
  const [axisScaleType, setAxisScaleType] = useState<AxisScaleType>("linear");

  const chartRef = useRef<RefObjectForHighchartsReact | null>(null);

  const tickerSymbolsForSeries = [...tickerSymbols];
  const selectedTickerSymbol = tickerSymbolsForSeries[0];
  if (!selectedTickerSymbol) {
    throw new Error("Couldn't locate the selected ticker symbol");
  }
  const { data, previousData, loading } = useQuery<
    IPerEmployeeRatiosHistoryQuery,
    IPerEmployeeRatiosHistoryQueryVariables
  >(PerEmployeeRatiosHistoryQuery, {
    variables: {
      tickerSymbol: tickerSymbols[0],
    },
  });
  const perEmployeeRatiosHistory =
    (loading && !data ? previousData?.perEmployeeRatiosHistory : data?.perEmployeeRatiosHistory) || [];

  if (!loading && perEmployeeRatiosHistory.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
          <div>
            <SadFaceIcon />
          </div>

          <p>
            <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
          </p>
        </div>
      </div>
    );
  }

  if (loading && perEmployeeRatiosHistory.length === 0) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  const years = perEmployeeRatiosHistory.map((node) => getYear(parseISO(node.asOfDate)));
  if (years.length < 3 || !isSequential(years.slice(-3))) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
          <div>
            <SadFaceIcon />
          </div>

          <p>
            <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
          </p>
        </div>
      </div>
    );
  }

  const labels = {
    totalOfEmployees: intl.formatMessage({
      defaultMessage: "Empregados",
      id: "gN3WmV",
    }),
    revenuePerEmployee: intl.formatMessage({
      defaultMessage: "Receita/Empregado",
      id: "qpiWEF",
    }),
  };

  const nodes = [
    {
      name: labels.totalOfEmployees,
      data: perEmployeeRatiosHistory
        .filter((node) => node.totalOfEmployees !== null)
        .map((node) => {
          return {
            x: getYear(parseISO(node.asOfDate)),
            y: node.totalOfEmployees,
            custom: node,
          };
        }),
    },
    {
      name: labels.revenuePerEmployee,
      data: perEmployeeRatiosHistory
        .filter((node) => node.revenuePerEmployee !== null)
        .map((node) => {
          return {
            x: getYear(parseISO(node.asOfDate)),
            y: node.revenuePerEmployee,
            custom: node,
          };
        }),
    },
  ];
  const series = nodes.map((node, index) => {
    const theme = getColorThemeForIndex(index);

    if (!theme) {
      throw new Error("Theme not found.");
    }

    const defaultOptions = {
      showInNavigator: true,
      type: plotType,
      animation: false,
      name: node.name,
      yAxis: index,
      color: theme.color,
      lineColor: theme.lineColor,
      lineWidth: 2,
      data: node.data.sort((a, b) => compareAsc(a.x, b.x)),
      custom: {
        tickerSymbol: selectedTickerSymbol,
        primaryColor: theme.color,
        secondaryColor: theme.lineColor,
      },
      marker: {
        width: 8,
        height: 8,
        lineWidth: 1,
        fillColor: theme.color,
        color: theme.color,
        lineColor: theme.lineColor,
        symbol: "circle",
      },
    };

    if (plotType === "column") {
      const columnOptions = {
        borderColor: theme.lineColor,
        borderWidth: 1.5,
        states: {
          hover: {
            color: theme.lineColor,
          },
        },
      };
      return { ...columnOptions, ...defaultOptions };
    }

    return defaultOptions;
  }) as (SeriesAreaOptions | SeriesBarOptions)[];

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: colors.slate[100],
    },
    scrollbar: {
      enabled: false,
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    series,
    legend: {
      align: "left",
      enabled: true,
      padding: 4,
      alignColumns: true,
      itemDistance: 16,
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        color: colors.slate[800],
        fontFamily: aeonik.style.fontFamily,
        fontWeight: "500",
        fontSize: "12px",
        width: 240,
        textOverflow: "ellipsis",
      },
      reversed: true,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: colors.slate[100],
      lineWidth: 1,
      lineColor: colors.slate[100],
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      labels: {
        step: 1,
        enabled: true,
        format: "{value}",
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
    },
    yAxis: nodes.map((node, index) => {
      const minValues = node.data.map((node) => node.y);
      const minValue = minValues.length > 0 ? Math.min(...minValues) : 0;

      return {
        type: axisScaleType,
        min: minValue,
        gridLineColor: colors.slate[100],
        lineWidth: 1,
        lineColor: colors.slate[100],
        labels: {
          enabled: true,
          align: "center",
          style: {
            color: colors.slate[500],
            fontFamily: aeonik.style.fontFamily,
            fontWeight: "500",
            fontSize: "12px",
          },
          formatter: function () {
            const valueFormatter = new Intl.NumberFormat("en", {
              notation: "compact",
              minimumFractionDigits: 0,
              maximumFractionDigits: 4,
            });
            return valueFormatter.format(parseFloat(String(this.value)));
          },
        },
        tickWidth: 0,
        showFirstLabel: true,
        showLastLabel: false,
        scrollbar: {
          enabled: false,
        },
        opposite: !!index,
      };
    }),
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      borderColor: colors.slate[200],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        const node = (this.point as any).custom as (typeof perEmployeeRatiosHistory)[number] | null | undefined;
        if (!this.key || !node || node.__typename !== "PerEmployeeRatiosHistoryItem") {
          return "";
        }

        const valueFormatter = new Intl.NumberFormat("en", {
          notation: "compact",
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });

        if (!this.points && this.point && this.key && typeof this.y === "number") {
          return renderToStaticMarkup(
            <div className="z-10 flex flex-col space-y-3 p-2 text-xs">
              <div className="grid auto-cols-auto grid-flow-col justify-between gap-x-4">
                <span className="font-mono font-semibold text-slate-600">{node.asOfDate}</span>
                <strong className="font-mono text-slate-700">{valueFormatter.format(this.y)}</strong>
              </div>
            </div>,
          );
        }

        if (!this.x || typeof this.x !== "number" || !this.points) {
          return renderToStaticMarkup(<span />);
        }

        return renderToStaticMarkup(
          <div className="z-10 flex flex-col space-y-3 p-2 text-xs">
            <span className="inline-flex justify-center rounded-lg bg-slate-50 px-2 py-1 font-mono text-sm font-bold text-slate-600">
              {node.asOfDate}
            </span>

            <ul className="space-y-1.5">
              {this.points.map((node, index) => {
                if (!node.y) {
                  return null;
                }

                const textColor =
                  node.series.userOptions && node.series.userOptions.custom
                    ? node.series.userOptions.custom.secondaryColor
                    : colors.slate[600];

                return (
                  <li
                    key={`${node.series.name}/${String(index)}`}
                    className="grid auto-cols-auto grid-flow-col justify-between gap-x-4"
                  >
                    <span
                      className="font-mono font-semibold"
                      style={{
                        color: textColor && typeof textColor === "string" ? textColor : colors.slate[600],
                      }}
                    >
                      {node.series.name}
                    </span>
                    <strong className="font-mono text-slate-700">{valueFormatter.format(node.y)}</strong>
                  </li>
                );
              })}
            </ul>
          </div>,
        );
      },
    },
    plotOptions: {
      series: {
        events: {
          legendItemClick(event) {
            handleClickInLegend(event, this, chartRef);
          },
        },
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: colors.slate[500],
            color: colors.slate[50],
            lineWidth: 2,
          },
        },
      },
      area: {
        color: colors.teal[50],
        lineColor: colors.teal[600],
        lineWidth: 2,
        dataLabels: {
          enabled: false,
        },
        marker: {
          enabled: false,
          fillColor: colors.teal[50],
          lineColor: colors.teal[600],
          lineWidth: 4,
          width: 8,
          height: 8,
        },
      },
      bar: {
        borderWidth: 1.5,
        borderRadius: {
          where: "end",
          radius: 12,
          scope: "stack",
        },
      },
    },
  };

  const hasZeroValue = perEmployeeRatiosHistory.some(
    (node) => node.totalOfEmployees === 0 || node.revenuePerEmployee === 0,
  );

  return (
    <div className="flex h-full w-full flex-col space-y-4">
      <div className="flex flex-col items-center justify-between sm:flex-row">
        <div className="grid w-full grid-cols-2 items-center gap-4 md:flex">
          <AreaBarPlotTypeSelector value={plotType} onChange={setPlotType} />
          {!hasZeroValue && <LogLinearScaleSelector value={axisScaleType} onChange={setAxisScaleType} />}

          {onToggleTickerSymbols && availableTickerSymbols && availableTickerSymbols.length >= 2 && (
            <div className="col-span-2 row-start-1">
              <TickerSymbolSelector
                tickerSymbols={availableTickerSymbols}
                value={tickerSymbols}
                onChange={(tickerSymbols) => onToggleTickerSymbols(tickerSymbols)}
              />
            </div>
          )}
        </div>
      </div>

      <HighchartsChart ref={chartRef} options={options} className="h-96 xl:h-full" />
    </div>
  );
}
